import { inSeason, seasonLabel } from '../utils/season';
import React from 'react';
import TrophySvg from './TrophySvg';
import { fmtTime12, fmtDate, fmtLabel, ttLabel } from '../utils/format';

/* This is the club-wide overview, reached from the player home. It had a
   "Today" strip across the top, which said the same thing as the Upcoming
   Matches column directly beneath it — twice on one screen, and on a quiet
   day the louder of the two was a banner announcing that nothing was on.
   The player home answers "what is on for me" now; this answers "what is the
   club doing", which the three columns already do. */
function DashboardView({ anchor, canCreateCompetition, clubs, compYearFilter, seasonStartMonth = 1, comps, currentUser, dashLeaderboard, deleteComp, endDateTouched, getEffectiveDefaults, isAdmin, isLoggedIn, isSuperAdmin, myRegistrations, openComp, openProfile, openTeamProfile, recentMatches, recentWinners, selectedClubId, setCompForm, setSelectedPreset, setShowCustomSettings, setView, upcomingMatches }) {
  /* Competitions and Results are both on this page, and the phone bar sends
     you here for either. Landing at the top both times leaves you hunting,
     so the caller says which part it meant and the page goes there. */
  React.useEffect(() => {
    if (!anchor) return;
    const el = document.getElementById(anchor);
    if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [anchor, comps, recentMatches]);

  const dashClubId = selectedClubId ? Number(selectedClubId) : (isSuperAdmin ? null : currentUser && currentUser.ClubId);
  const dashClub = clubs.find(c => c.id === dashClubId);
  const dashLogo = dashClub && dashClub.logo;

  const startNewComp = () => {
    setView('newComp');
    setSelectedPreset(null);
    setCompForm(getEffectiveDefaults());
    setShowCustomSettings(true);
    endDateTouched.current = false;
  };

  return (
    <div className="page-container">
      {dashLogo && <>
        <div style={{ position: 'fixed', inset: 0, backgroundImage: `url(${dashLogo})`, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: -2 }} />
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.82)', zIndex: -1, pointerEvents: 'none' }} />
      </>}

      <div className="dash-columns">
        {/* ── COLUMN 1: Competitions ── */}
        <div className="dash-col">
          <div className="w3-card w3-round w3-white" id="overview-competitions">
            <div className="dash-card-header w3-theme-d4 w3-round-top">
              <span style={{ fontWeight: 700 }}><TrophySvg size={16} color="white" style={{ marginRight: '8px' }} />Competitions ({comps.filter(c => c.status !== 'completed').length})</span>
              {/* Adding a competition belongs to this list, so the button sits
                  on it rather than in a banner of its own. */}
              {isAdmin && (
                <button className="w3-button w3-round dash-add-comp"
                  disabled={!canCreateCompetition}
                  title={!canCreateCompetition ? 'Select a specific club instead of All Clubs to create a competition' : 'Add a competition'}
                  onClick={startNewComp}>
                  <i className="fa fa-plus w3-margin-right"></i>Add
                </button>
              )}
            </div>

            <div className="w3-container w3-padding">
              {comps.filter(c => c.status !== 'completed').filter(c => !c.startDate || inSeason(c.startDate, compYearFilter, seasonStartMonth)).length === 0 ? (
                <p className="w3-text-grey w3-center" style={{ padding: '16px 0' }}>
                  <TrophySvg size={40} color="silver" style={{ display: 'block', marginBottom: '8px', margin: '0 auto 8px' }} />
                  <em>No upcoming competitions.</em>
                </p>
              ) : (
                <div className="dash-comp-list">
                  {[...comps].filter(c => c.status !== 'completed').filter(c => !c.startDate || inSeason(c.startDate, compYearFilter, seasonStartMonth)).sort((a, b) => (a.startDate || '').localeCompare(b.startDate || '')).map(c => (
                    <div key={c.id} className="dash-comp-row w3-hover-light-grey"
                      onClick={() => openComp(c)} style={{ cursor: 'pointer' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 'var(--gs-text-sm)' }}>{c.name}</div>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px', alignItems: 'center' }}>
                          <span className="w3-tag w3-round w3-theme-l3" style={{ fontSize: 'var(--gs-text-xs)', padding: '1px 8px' }}>{fmtLabel(c.format)}</span>
                          <span className="w3-tag w3-round w3-light-grey" style={{ fontSize: 'var(--gs-text-xs)', padding: '1px 8px' }}>{ttLabel(c.tournamentType || 'round-robin')}</span>
                          <span className={`w3-tag w3-round ${c.status === 'in-progress' ? 'w3-green' : c.status === 'completed' ? 'w3-blue' : 'w3-grey'}`}
                            style={{ fontSize: 'var(--gs-text-xs)', padding: '1px 8px' }}>{c.status || 'draft'}</span>
                          {isLoggedIn && !isAdmin && (() => {
                            const reg = myRegistrations[c.id];
                            if (reg && reg.status === 'approved') return <span className="w3-tag w3-round w3-green" style={{ fontSize: 'var(--gs-text-xs)', padding: '1px 8px' }}><i className="fa fa-check w3-margin-right" style={{ fontSize: 'var(--gs-text-sm)' }}></i>Registered</span>;
                            if (reg && reg.status === 'pending') return <span className="w3-tag w3-round w3-orange" style={{ fontSize: 'var(--gs-text-xs)', padding: '1px 8px' }}><i className="fa fa-clock-o w3-margin-right" style={{ fontSize: 'var(--gs-text-sm)' }}></i>Pending</span>;
                            return <span className="w3-tag w3-round w3-blue" style={{ fontSize: 'var(--gs-text-xs)', padding: '1px 8px' }}><i className="fa fa-user-plus w3-margin-right" style={{ fontSize: 'var(--gs-text-sm)' }}></i>Register</span>;
                          })()}
                          {c.startDate && <span className="w3-tiny w3-text-grey" style={{ marginLeft: '4px' }}><i className="fa fa-calendar"></i> {fmtDate(c.startDate)}{c.endDate && c.endDate !== c.startDate ? ` — ${fmtDate(c.endDate)}` : ''}</span>}
                        </div>
                      </div>
                      {isAdmin && c.status !== 'completed' && !c.drawFinalised && <button className="w3-button w3-tiny w3-text-grey w3-hover-red w3-round"
                        onClick={e => { e.stopPropagation(); deleteComp(c.id); }}
                        title="Delete" aria-label="Delete" style={{ padding: '2px 8px' }}>
                        <i className="fa fa-trash"></i>
                      </button>}
                      <i className="fa fa-chevron-right w3-text-light-grey"></i>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Completed Competitions */}
          {comps.filter(c => c.status === 'completed').filter(c => !c.startDate || inSeason(c.startDate, compYearFilter, seasonStartMonth)).length > 0 && (
            <div className="w3-card w3-round w3-white" style={{ marginTop: '16px' }}>
              <div className="dash-card-header w3-theme-l4 w3-round-top">
                <span style={{ fontWeight: 700 }}><i className="fa fa-check-circle w3-margin-right"></i>Past Competitions ({comps.filter(c => c.status === 'completed').filter(c => !c.startDate || inSeason(c.startDate, compYearFilter, seasonStartMonth)).length})</span>
              </div>
              <div className="w3-container w3-padding">
                <div className="dash-comp-list">
                  {[...comps].filter(c => c.status === 'completed').filter(c => !c.startDate || inSeason(c.startDate, compYearFilter, seasonStartMonth)).sort((a, b) => (b.endDate || b.startDate || '').localeCompare(a.endDate || a.startDate || '')).map(c => (
                    <div key={c.id} className="dash-comp-row w3-hover-light-grey"
                      onClick={() => openComp(c)} style={{ cursor: 'pointer' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 'var(--gs-text-sm)' }}>{c.name}</div>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px', alignItems: 'center' }}>
                          <span className="w3-tag w3-round w3-theme-l3" style={{ fontSize: 'var(--gs-text-xs)', padding: '1px 8px' }}>{fmtLabel(c.format)}</span>
                          <span className="w3-tag w3-round w3-light-grey" style={{ fontSize: 'var(--gs-text-xs)', padding: '1px 8px' }}>{ttLabel(c.tournamentType || 'round-robin')}</span>
                          <span className="w3-tag w3-round w3-blue" style={{ fontSize: 'var(--gs-text-xs)', padding: '1px 8px' }}>completed</span>
                          {c.startDate && <span className="w3-tiny w3-text-grey" style={{ marginLeft: '4px' }}><i className="fa fa-calendar"></i> {fmtDate(c.startDate)}{c.endDate && c.endDate !== c.startDate ? ` — ${fmtDate(c.endDate)}` : ''}</span>}
                        </div>
                      </div>
                      {isAdmin && c.status !== 'completed' && !c.drawFinalised && <button className="w3-button w3-tiny w3-text-grey w3-hover-red w3-round"
                        onClick={e => { e.stopPropagation(); deleteComp(c.id); }}
                        title="Delete" aria-label="Delete" style={{ padding: '2px 8px' }}>
                        <i className="fa fa-trash"></i>
                      </button>}
                      <i className="fa fa-chevron-right w3-text-light-grey"></i>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── COLUMN 2: Upcoming Matches ── */}
        <div className="dash-col">
          <div className="w3-card w3-round w3-white">
            <div className="dash-card-header w3-theme-d4 w3-round-top">
              <span style={{ fontWeight: 700 }}><i className="fa fa-calendar w3-margin-right"></i>Upcoming Matches</span>
            </div>
            <div className="w3-container w3-padding">
              {(() => { const filtered = upcomingMatches.filter(m => compYearFilter === 'all' || (m.compStartDate && inSeason(m.compStartDate, compYearFilter, seasonStartMonth))); return filtered.length === 0 ? (
                <p className="w3-text-grey w3-center" style={{ padding: '16px 0' }}>
                  <i className="fa fa-calendar-check-o w3-xxlarge w3-text-light-grey" style={{ display: 'block', marginBottom: '8px' }}></i>
                  <em>No upcoming matches.</em>
                </p>
              ) : (
                <div className="upcoming-list">
                  {filtered.map(m => (
                    <div key={m.id} className="upcoming-row"
                      onClick={() => { const c = comps.find(x => x.id === m.compId); if (c) openComp(c); }}
                      style={{ cursor: 'pointer' }}>
                      <div className="upcoming-meta">
                        {(() => { const colors = ['#e91e63','#9c27b0','#3f51b5','#009688','#ff5722','#2196f3','#4caf50','#ff9800','#795548','#607d8b']; const bg = colors[(m.compId || 0) % colors.length]; return <span className="w3-tag w3-round" style={{ fontSize: 'var(--gs-text-xs)', padding: '0 6px', background: bg, color: '#fff' }}>{m.compName}</span>; })()}
                        <span className="w3-text-grey" style={{ fontSize: 'var(--gs-text-xs)' }}>Round {m.round}{(m.compShowGreen && m.green) ? ` · G${m.green}` : ''}{(m.compShowRink && m.rink) ? ` R${m.rink}` : ''}{m.time ? ` · ${fmtTime12(m.time)}` : ''}{(() => { const d = m.date || (m.compRoundStartDates && m.compRoundStartDates[m.round]) || m.compStartDate; return d ? ` · ${fmtDate(d)}` : ''; })()}</span>
                      </div>
                      <div className="upcoming-teams">
                        <span style={{ fontWeight: 600, fontSize: 'var(--gs-text-sm)' }}>{m.HomeTeam ? m.HomeTeam.name : 'TBD'}</span>
                        <span className="w3-text-grey" style={{ fontSize: 'var(--gs-text-xs)', margin: '0 6px' }}>vs</span>
                        <span style={{ fontWeight: 600, fontSize: 'var(--gs-text-sm)' }}>{m.AwayTeam ? m.AwayTeam.name : 'TBD'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ); })()}
            </div>
          </div>

          {/* Recent Results */}
          <div className="w3-card w3-round w3-white w3-margin-top" id="overview-results">
            <div className="dash-card-header w3-theme-d4 w3-round-top">
              <span style={{ fontWeight: 700 }}><TrophySvg size={16} color="white" style={{ marginRight: '8px' }} />Recent Results</span>
            </div>
            <div className="w3-container" style={{ padding: '8px 16px' }}>
              {(() => { const filtered = recentMatches.filter(m => compYearFilter === 'all' || (m.compStartDate && inSeason(m.compStartDate, compYearFilter, seasonStartMonth))).slice(0, 10); return filtered.length === 0 ? (
                <p className="w3-text-grey w3-center" style={{ padding: '16px 0' }}>
                  <TrophySvg size={40} color="silver" style={{ display: 'block', marginBottom: '8px', margin: '0 auto 8px' }} />
                  <em>No recent results.</em>
                </p>
              ) : (
                <div>
                  {filtered.map(m => {
                    const hScore = m.homeScore ?? 0;
                    const aScore = m.awayScore ?? 0;
                    const homeName = m.HomeTeam ? m.HomeTeam.name : 'TBD';
                    const awayName = m.AwayTeam ? m.AwayTeam.name : 'TBD';
                    const homeWon = m.result === 'win-home';
                    const awayWon = m.result === 'win-away';
                    const colors = ['#e91e63','#9c27b0','#3f51b5','#009688','#ff5722','#2196f3','#4caf50','#ff9800','#795548','#607d8b'];
                    const bg = colors[(m.compId || 0) % colors.length];
                    return (
                    <div key={m.id}
                      onClick={() => { const c = comps.find(x => x.id === m.compId); if (c) openComp(c); }}
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid #eee', fontSize: '0.82em' }}>
                      <span style={{ width: '30%', display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, overflow: 'hidden' }}>
                        <span className="w3-tag w3-round" style={{ fontSize: 'var(--gs-text-xs)', padding: '0 5px', background: bg, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.compName}</span>
                        <span className="w3-text-grey" style={{ fontSize: 'var(--gs-text-xs)', whiteSpace: 'nowrap' }}>R{m.round}</span>
                      </span>
                      <span style={{ width: '25%', textAlign: 'right', fontWeight: homeWon ? 700 : 400, color: homeWon ? '#2e7d32' : undefined, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: '6px' }}>{homeWon && <TrophySvg size={11} color="gold" style={{ marginRight: '4px' }} />}{homeName}</span>
                      <span style={{ width: '20%', fontWeight: 600, textAlign: 'center', whiteSpace: 'nowrap' }}>{hScore} – {aScore}</span>
                      <span style={{ width: '25%', fontWeight: awayWon ? 700 : 400, color: awayWon ? '#2e7d32' : undefined, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingLeft: '6px' }}>{awayName}{awayWon && <TrophySvg size={11} color="gold" style={{ marginLeft: '4px' }} />}</span>
                    </div>
                    );
                  })}
                </div>
              ); })()}
            </div>
          </div>
        </div>

        {/* ── COLUMN 3: Leaderboard & Winners ── */}
        <div className="dash-col">
          {/* Leaderboard */}
          <div className="w3-card w3-round w3-white w3-margin-bottom">
            <div className="dash-card-header w3-theme-d4 w3-round-top" style={{ cursor: 'pointer' }} onClick={() => setView('clubStats')}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                <span style={{ fontWeight: 700 }}><i className="fa fa-list-ol w3-margin-right"></i>Leaderboard</span>
                <span style={{ fontSize: 'var(--gs-text-xs)', opacity: 0.85, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}><TrophySvg size={12} color="white" style={{ marginRight: '8px' }} />{compYearFilter === 'all' ? 'All-time' : `${seasonLabel(compYearFilter, seasonStartMonth)} competitions`}</span>
              </div>
            </div>
            <div className="w3-container w3-padding">
              {!dashLeaderboard || !dashLeaderboard.standings || dashLeaderboard.standings.length === 0 ? (
                <p className="w3-text-grey w3-center" style={{ padding: '16px 0' }}>
                  <i className="fa fa-bar-chart w3-xxlarge w3-text-light-grey" style={{ display: 'block', marginBottom: '8px' }}></i>
                  <em>No results yet.</em>
                </p>
              ) : (
                <div>
                  <table className="w3-table w3-small w3-bordered">
                    <thead>
                      <tr className="w3-theme-l4">
                        <th style={{ width: '28px' }}>#</th>
                        <th>Name</th>
                        <th style={{ width: '50px' }}>Rating</th>
                        <th style={{ width: '28px' }}>P</th>
                        <th style={{ width: '28px' }}>W</th>
                        <th style={{ width: '28px' }}>L</th>
                        <th style={{ width: '40px' }}>Win%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashLeaderboard.standings.slice(0, 10).map((s, i) => (
                        <tr key={`${s.type}-${s.id}`}
                          className="w3-hover-light-grey" style={{ cursor: 'pointer' }}
                          onClick={() => { if (s.type === 'player') openProfile(s.id); else openTeamProfile(s.id); }}>
                          <td style={{ fontWeight: 700 }}>{i + 1}</td>
                          <td style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}>
                            <i className={`fa ${s.type === 'team' ? 'fa-shield' : 'fa-user'} w3-margin-right w3-small`}
                              style={{ color: s.type === 'team' ? '#607d8b' : '#999', width: '14px' }}></i>
                            {s.name}
                          </td>
                          <td style={{ fontWeight: 700 }}>{s.rating ?? '–'}</td>
                          <td>{s.played}</td>
                          <td className="w3-text-green">{s.wins}</td>
                          <td className="w3-text-red">{s.losses}</td>
                          <td>{s.played > 0 ? Math.round((s.wins / s.played) * 100) : 0}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {dashLeaderboard.standings.length > 10 && (
                    <p className="w3-small w3-text-theme w3-center" style={{ margin: '8px 0 0', cursor: 'pointer' }}
                      onClick={() => setView('clubStats')}>
                      <em>+ {dashLeaderboard.standings.length - 10} more… <i className="fa fa-chevron-right"></i></em>
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Recent Winners */}
          <div className="w3-card w3-round w3-white w3-margin-top">
            <div className="dash-card-header w3-theme-d4 w3-round-top">
              <span style={{ fontWeight: 700 }}><TrophySvg size={16} color="white" style={{ marginRight: '8px' }} />Recent Winners</span>
            </div>
            <div className="w3-container w3-padding">
              {(() => { const winners = Array.isArray(recentWinners) ? recentWinners : []; const filtered = winners.filter(w => compYearFilter === 'all' || (w.endDate && inSeason(w.endDate, compYearFilter, seasonStartMonth))); return filtered.length === 0 ? (
                <p className="w3-text-grey w3-center" style={{ padding: '16px 0' }}>
                  <TrophySvg size={40} color="silver" style={{ display: 'block', marginBottom: '8px', margin: '0 auto 8px' }} />
                  <em>No completed competitions yet.</em>
                </p>
              ) : (
                <div>
                  {filtered.map(w => (
                    <div key={w.competitionId} className="w3-hover-light-grey" style={{ padding: '8px 4px', borderBottom: '1px solid #eee', cursor: 'pointer' }}
                      onClick={() => { const c = comps.find(x => x.id === w.competitionId); if (c) openComp(c); }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <TrophySvg size={20} color="gold" />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 'var(--gs-text-sm)' }}>{w.winner.players && w.winner.players.length > 0 ? w.winner.players.join(', ') : w.winner.teamName}</div>
                          <div className="w3-tiny w3-text-grey">
                            {w.competitionName}
                            {w.endDate ? ` — ${fmtDate(w.endDate)}` : ''}
                          </div>
                          <div className="w3-tiny w3-text-grey">
                            W{w.winner.wins} L{w.winner.losses}{w.winner.draws > 0 ? ` D${w.winner.draws}` : ''} · {w.winner.gamePoints} pts
                          </div>
                        </div>
                        <div>
                          <span className="w3-tag w3-round w3-theme-l3" style={{ fontSize: 'var(--gs-text-xs)', padding: '2px 8px' }}>{w.format}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ); })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardView;
